/**
 * The drawer's preview of a credit, in integer cents with BigInt. The web has no
 * decimal library and money never becomes a JS number (CLAUDE.md §7.3). This
 * mirrors `creditLineAmount` in the API: quantities in thousandths, the line's
 * share rounded half up but never more than earlier notes left of its net, and
 * the last quantity on a line takes that remainder. The server is authoritative;
 * this only previews.
 */

export interface PreviewLine {
  qty: string;
  creditedQty: string;
  creditedAmount: string;
  /** The line total after its own discount. */
  netTotal: string;
  /** The quantity being credited now, as typed. Blank counts as zero. */
  creditNow: string;
  /** Whether this line's credit sends a part back to the shelf. */
  backToStock: boolean;
}

export interface PreviewInvoice {
  grandTotal: string;
  creditedTotal: string;
  paidTotal: string;
}

export interface CreditPreview {
  /** Decimal strings with two places. */
  credit: string;
  newTotal: string;
  beyondTotal: string;
  /** Quantity going back to the shelf, a decimal string with up to three places. */
  partsBack: string;
}

/** A decimal string as an integer scaled by 10^dp, truncating extra places. */
function scaled(value: string, dp: number): bigint {
  const trimmed = value.trim();
  if (trimmed === '') return 0n;
  const negative = trimmed.startsWith('-');
  const [whole = '0', fraction = ''] = trimmed.replace('-', '').split('.');
  const digits = `${whole === '' ? '0' : whole}${fraction.padEnd(dp, '0').slice(0, dp)}`;
  const n = BigInt(digits);
  return negative ? -n : n;
}

function unscaled(value: bigint, dp: number): string {
  const negative = value < 0n;
  const digits = (negative ? -value : value).toString().padStart(dp + 1, '0');
  const whole = digits.slice(0, digits.length - dp);
  const fraction = digits.slice(digits.length - dp);
  return `${negative ? '-' : ''}${whole}${dp === 0 ? '' : `.${fraction}`}`;
}

/** What crediting `creditNow` units takes off one line, in cents. */
export function lineCreditCents(line: PreviewLine): bigint {
  const creditQty = scaled(line.creditNow, 3);
  const lineQty = scaled(line.qty, 3);
  if (creditQty <= 0n || lineQty <= 0n) return 0n;
  const net = scaled(line.netTotal, 2);
  const left = net - scaled(line.creditedAmount, 2);
  if (creditQty + scaled(line.creditedQty, 3) === lineQty) return left;
  // Round half up: (2·net·qty + lineQty) / (2·lineQty) for non-negative net.
  const share = (2n * net * creditQty + lineQty) / (2n * lineQty);
  return share < left ? share : left;
}

/** The same amount as `lineCreditCents`, as a two-place decimal string. */
export function lineCreditAmount(line: PreviewLine): string {
  return unscaled(lineCreditCents(line), 2);
}

export function creditPreview(lines: PreviewLine[], invoice: PreviewInvoice): CreditPreview {
  let credit = 0n;
  let parts = 0n;
  for (const line of lines) {
    const cents = lineCreditCents(line);
    if (cents === 0n && scaled(line.creditNow, 3) <= 0n) continue;
    credit += cents;
    if (line.backToStock) parts += scaled(line.creditNow, 3);
  }
  const newTotal = scaled(invoice.grandTotal, 2) - scaled(invoice.creditedTotal, 2) - credit;
  const beyond = scaled(invoice.paidTotal, 2) - newTotal;
  return {
    credit: unscaled(credit, 2),
    newTotal: unscaled(newTotal, 2),
    beyondTotal: unscaled(beyond > 0n ? beyond : 0n, 2),
    partsBack: unscaled(parts, 3).replace(/\.?0+$/, ''),
  };
}
